import { useCallback, useEffect, useState } from 'react';

/**
 * Sticky `top` offset for a sidebar that can be TALLER than the viewport.
 *
 * A plain `sticky top-24` pins the sidebar's top edge, which leaves its
 * lower cards unreachable until the page's grid row ends at the very bottom
 * of the page (TELV1-542). An internal `max-h + overflow-y-auto` instead
 * creates two competing scroll contexts (the TELV1-201 bug this replaced).
 *
 * The bounded third option: while the sidebar fits the viewport it sticks
 * at `topOffsetPx`, exactly like `top-24`. When it outgrows the viewport,
 * the sticky anchor moves up (negative `top`) by the overflow, so the
 * sidebar scrolls with the page until its BOTTOM edge is in view and only
 * then sticks — one scroll context, every card reachable.
 *
 * Returns a CALLBACK ref (not a RefObject): both consumers mount their
 * sidebar conditionally (loading early-returns, selected-question guard),
 * so the measurement must re-arm whenever the element actually appears.
 */
export const useBottomAwareStickyTop = (
  topOffsetPx = 96, // 6rem — matches the `top-24` header clearance it replaces
  bottomMarginPx = 20,
) => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [top, setTop] = useState(topOffsetPx);

  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);

  useEffect(() => {
    // jsdom has no ResizeObserver — same guard as ProjectResults.tsx.
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const overflow = el.offsetHeight + topOffsetPx + bottomMarginPx - window.innerHeight;
      setTop(overflow > 0 ? topOffsetPx - overflow : topOffsetPx);
    };

    update();
    // Sidebar content is dynamic (accordions, quota widgets, loading states):
    // re-measure whenever the sidebar's own size changes, not just on resize.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);
    // Late-layout insurance: fonts/images landing after mount change the
    // height before the observer's first callback in throttled/hidden
    // contexts — one delayed re-measure catches the settled layout.
    const raf = requestAnimationFrame(update);
    const settle = setTimeout(update, 300);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      cancelAnimationFrame(raf);
      clearTimeout(settle);
    };
  }, [el, topOffsetPx, bottomMarginPx]);

  return { ref, top };
};
